import React, { useState, useCallback, useEffect, useRef } from 'react';
import { GoogleMap, Marker, Polyline, OverlayView } from '@react-google-maps/api';
import i18nDisplay from '../utils/i18nDisplay';
import { useTranslation } from 'react-i18next';

const defaultContainerStyle = {
  width: '100vw',
  height: '79vh',
};

const defaultOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: false,
  fullscreenControl: true,
  streetViewControl: false,
};

const BookingPopup = ({ booking }) => {
  const { t } = useTranslation(['dashboard']);
  if (!booking) return null;
  
  const isDuo = booking.therapists?.length == 2;
  
  return (
    <div className="booking-popup">
      <div className="booking-header">
        <h4>{i18nDisplay(booking?.hotel ? booking?.hotel?.name : booking?.client?.extra?.full_name) || 'Unknown Client'}</h4>
        {isDuo && <span className="duo-tag">DUO</span>}
      </div>
      <h5>{i18nDisplay(booking?.hotel ? `${booking?.client?.roles?.some(r => r.name !== 'ROLE_CLIENT') ? t('internal')+': ' : t('external')+': '} ${booking?.client?.extra?.full_name || ''}` : '')}</h5>
      
      <div className="services-section">
        <strong>Services:</strong>
        {booking?.products?.length > 0 ? booking.products.map((product, index) => (
          <div key={index} className="service-item">
            <span className="service-name">{i18nDisplay(product?.category?.title)}</span>
            {product?.category && (
              <span className="service-category">
                {i18nDisplay(product?.name)}
              </span>
            )}
            {product?.duration && (
              <span className="service-duration">{product.duration} min</span>
            )}
          </div>
        )) : (
          <div className="service-item">
            <span className="service-name">No services available</span>
          </div>
        )}
      </div>
      
      {booking.therapists?.length > 0 && (
        <p>
          <strong>flypro:</strong>{' '}
          {booking.therapists.map(t => i18nDisplay(t?.extra?.full_name) || 'Unknown').join(' , ')}
        </p>
      )}
      
      <p>
        <strong>Address:</strong>{' '}
        {[
          i18nDisplay(booking.address?.street_number),
          i18nDisplay(booking.address?.street),
          i18nDisplay(booking.address?.zip_code),
          i18nDisplay(booking.address?.state)
        ].filter(Boolean).join(' ') || 'Address not available'}
      </p>
    </div>
  );
};

const CustomMarker = ({ position, flypro, onMouseOut, onMouseOver }) => {
  
  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <div 
        className="map-marker"
        onMouseEnter={onMouseOver}
        onMouseLeave={onMouseOut}
        // onClick={onClick}
        style={{ cursor: 'pointer' }}
      >
        <div className="marker-body hotel-item">
          <img 
            className='hotel-image' 
            src={flypro?.media?.length ? flypro.media[0]?.original_url : '/assets/images/dashboard-home-page-image.png'} 
            alt='Flypro_image'
            onError={(e) => {
              e.target.src = '/assets/images/dashboard-home-page-image.png';
            }}
          />
          {/* <div className='hotel-details'>
            <span className='hotel-name'>{flypro?.firstname || 'Unknown FlyPRO'}</span>
          </div> */}
        </div>
        <img src='/assets/images/tail.svg' alt='images' className="marker-tail" />
      </div>
    </OverlayView>
  );
};

const CustomMarkerTransit = ({ position, flypro }) => {
  if (!flypro || !position) return null;
  
  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <div className="map-marker transit-flypro">
        <div className="marker-body hotel-item">
          <img 
            className='hotel-image' 
            src={flypro?.media?.length ? flypro.media[0]?.original_url : '/assets/images/dashboard-home-page-image.png'} 
            alt='Flypro_image'
            onError={(e) => {
              e.target.src = '/assets/images/dashboard-home-page-image.png';
            }}
          />
        </div>
        <img src='/assets/images/tail.svg' alt='images' className="marker-tail" />
      </div>
    </OverlayView>
  );
};

export const Map = ({
  coordinates = [], // List of coordinates
  mode = 'view', // Can be 'view' or 'edit'
  onCoordinateChange = () => { }, // Callback to return updated coordinates
  lat,
  lng,
  zoom,
  mapOptions = defaultOptions, // Configurable map options
  containerStyle = defaultContainerStyle, // Parametrable container style with default values
  routeMarkers = [],
  selectedDate,
  centerSkip = false,
  onMarkerClick = () => { },
}) => {
  const [markers, setMarkers] = useState([]);
  const [directions, setDirections] = useState(null);
  const [hoveredBooking, setHoveredBooking] = useState(null);
  const [hoveredFlypro, setHoveredFlypro] = useState(null);

  useEffect(() => {
    if (
      routeMarkers.length === 2 &&
      routeMarkers[0]?.lat &&
      routeMarkers[1]?.lat
    ) {
      const directionsService = new window.google.maps.DirectionsService();

      directionsService.route(
        {
          origin: { lat: routeMarkers[0].lat, lng: routeMarkers[0].lng },
          destination: { lat: routeMarkers[1].lat, lng: routeMarkers[1].lng },
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            setDirections(result);
          } else {
            console.error("Directions request failed due to ", status);
            setDirections(null);
          }
        }
      );
    } else {
      setDirections(null);
    }
  }, [JSON.stringify(routeMarkers)]);




  // console.log('Map props:', lat, lng, zoom);
  const MIN_DISTANCE_THRESHOLD = 0.0001; // ~11 meters

  const offsetCoordinatesIfTooClose = (coords) => {
    const adjusted = [...coords];
    
    for (let i = 0; i < adjusted.length; i++) {
      for (let j = i + 1; j < adjusted.length; j++) {
        const dx = adjusted[i].lat - adjusted[j].lat;
        const dy = adjusted[i].lng - adjusted[j].lng;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < MIN_DISTANCE_THRESHOLD) {
          const offsetX = (j % 2 === 0 ? 1 : -1) * 0.0001;
          const offsetY = (Math.floor(j / 2) % 2 === 0 ? 1 : -1) * 0.0001;
          
          adjusted[j] = {
            lat: adjusted[j].lat + Math.random() * 0.0002 - 0.0001,
            lng: adjusted[j].lng + Math.random() * 0.0002 - 0.0001,
          };
        }
      }
    }
    return adjusted;
  };


  useEffect(() => {
    if (coordinates.length > 0) {
      // const adjustedCoords = offsetCoordinatesIfTooClose(coordinates);
      // setMarkers(adjustedCoords);
      console.log({ coordinates });
      setMarkers(coordinates);
    } else {
      setMarkers([]);
    }
  }, [coordinates]);


  const handleMarkerClick = useCallback((coordinate) => {
    onMarkerClick(coordinate);
  }, [onMarkerClick]);

  // const handleMarkerHover = useCallback((coordinate, isHover) => {
  //   if (coordinate.booking) {
  //     setHoveredBooking(isHover ? coordinate.booking : null);
  //   } else if (coordinate.flypro) {
  //     setHoveredFlypro(isHover ? coordinate.flypro : null);
  //   }
  // }, []);

  const handleDragEnd = useCallback(
    (event, index) => {
      const updatedCoordinates = [...markers];
      updatedCoordinates[index] = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };
      setMarkers(updatedCoordinates);
      onCoordinateChange(updatedCoordinates);
    },
    [markers, onCoordinateChange]
  );

  return (
    <div className="map-container">
      <GoogleMap
        mapContainerStyle={containerStyle} 
        // center={mode === 'edit' && markers.length === 1 ? markers[0] : markers[0] || { lat: 46.2643, lng: 6.3166 }}
        center={centerSkip ? undefined : (mode === 'edit' && markers.length === 1 ? markers[0] : markers.length <= 2 ? markers[0] : { lat: 46.3860932, lng: 6.3907335 } || { lat: 46.3860932, lng: 6.3907335 })}
        zoom={zoom || 15}
        options={{
          ...mapOptions,
          // gestureHandling: 'greedy'
        }}
      >
        {!directions && markers.map((coordinate, index) => {
          if (coordinate?.flypro && !selectedDate) {
            return (
              <React.Fragment key={`flypro-${index}-${coordinate.flypro?.id || index}`}>
                <CustomMarker
                  position={coordinate}
                  flypro={coordinate.flypro}
                  // onMouseOver={() => handleMarkerHover(coordinate, true)}
                  // onMouseOut={() => handleMarkerHover(coordinate, false)}
                  // onClick={() => handleMarkerClick(coordinate)}
                  onMouseOver={() => setHoveredFlypro(coordinate.flypro)}
                  onMouseOut={() => setTimeout(() => setHoveredFlypro(null), 500)}
                />
                {hoveredFlypro === coordinate.flypro && (
                  <OverlayView
                    position={coordinate}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div className="booking-popup flypro-popup">
                      <h4>{coordinate.flypro?.firstname} {coordinate.flypro?.lastname}</h4>
                      {/* <p><strong>FlyPRO</strong></p>
                      {coordinate.flypro?.specialties && (
                        <p><strong>Specialties:</strong> {coordinate.flypro.specialties.join(', ')}</p>
                      )} */}
                    </div>
                  </OverlayView>
                )}
              </React.Fragment>
            );
          } else if (coordinate?.booking) {
            return (
              <React.Fragment key={`booking-${index}-${coordinate.booking?.id || index}`}>
                <Marker
                // key={index}
                position={coordinate}
                // draggable={mode === 'edit' && markers.length === 1}
                onClick={() => onMarkerClick(coordinate)}
                onDragEnd={(event) => handleDragEnd(event, index)}
                onMouseOver={() => setHoveredBooking(coordinate.booking)}
                onMouseOut={() => setTimeout(() => setHoveredBooking(null), 500)}
              />

                {hoveredBooking === coordinate.booking && (
                  <OverlayView
                    position={coordinate}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <BookingPopup booking={coordinate.booking} />
                  </OverlayView>
                )}
              </React.Fragment>
            );
          } else {
            return (
              <Marker
                key={`marker-${index}`}
                position={coordinate}
                // draggable={mode === 'edit' && markers.length === 1}
                onClick={() => onMarkerClick(coordinate)}
                onDragEnd={(event) => handleDragEnd(event, index)}
              />
            );
          }
        })}
        {/* {directions && <DirectionsRenderer directions={directions} />} */}
        {directions && (
          <>
            {directions.routes?.[0]?.legs?.[0]?.start_location && (
              <Marker
                position={directions.routes[0].legs[0].start_location}
                icon={{
                  url: 'https://maps.google.com/mapfiles/kml/paddle/wht-blank.png',
                }}
              />
            )}

            {directions.routes?.[0]?.legs?.[0]?.end_location && (
              <Marker
                position={directions.routes[0].legs[0].end_location}
                icon={{
                  url: 'https://maps.google.com/mapfiles/kml/paddle/blu-blank.png',
                }}
              />
            )}
            
            {directions.routes?.[0]?.legs?.[0]?.start_location && routeMarkers[0]?.flypro && (
              <CustomMarkerTransit
                position={directions.routes[0].legs[0].start_location}
                flypro={routeMarkers[0].flypro}
              />
            )}
            
            {directions.routes?.[0]?.overview_path && (
              <Polyline
                path={directions.routes[0].overview_path}
                options={{
                  strokeColor: '#4285F4',
                  strokeOpacity: 0.8,
                  strokeWeight: 5,
                }}
              />
            )}
          </>
        )}

      </GoogleMap>
    </div>
  );
};